import { FloatingPortal, offset, useClick, useDismiss, useFloating, useInteractions } from '@floating-ui/react'
import { Popover } from '@headlessui/react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Calendar as CalendarIcon, RefreshDouble, WarningTriangle } from 'iconoir-react'
import moment from 'moment'
import { useMemo, useState } from 'react'
import Button from 'app/components/Button'
import CalendarPicker from 'app/components/marketing/CalendarPicker'
import { PageHeader } from 'app/components/PageHeader'
import { Tooltip } from 'app/components/Tooltip'
import { useDeferredDataWithStatus } from 'app/hooks/useDeferredData'

// Colors for appointment types (from tag colors, excluding grays)
const APPOINTMENT_TYPE_COLORS = [
  { bg: `bg-tags-blue`, border: `border-l-tags-blue`, name: `Blue` },
  { bg: `bg-tags-green`, border: `border-l-tags-green`, name: `Green` },
  { bg: `bg-tags-purple`, border: `border-l-tags-purple`, name: `Purple` },
  { bg: `bg-tags-orange`, border: `border-l-tags-orange`, name: `Orange` },
  { bg: `bg-tags-pink`, border: `border-l-tags-pink`, name: `Pink` },
  { bg: `bg-tags-salmon`, border: `border-l-tags-salmon`, name: `Salmon` },
  { bg: `bg-tags-yellow`, border: `border-l-tags-yellow`, name: `Yellow` },
  { bg: `bg-tags-red`, border: `border-l-tags-red`, name: `Red` },
  { bg: `bg-tags-amber`, border: `border-l-tags-amber`, name: `Amber` },
]

// Time slots from 6:00 to 20:00 in 30-min increments
const TIME_SLOTS = Array.from({ length: 29 }, (_, i) => {
  const hour = Math.floor(i / 2) + 6
  const min = (i % 2) * 30
  return `${hour.toString().padStart(2, `0`)}:${min.toString().padStart(2, `0`)}`
})

// Exported so route files and tests can reference these types
export { APPOINTMENT_TYPE_COLORS }

export type OperatoryData = {
  id: string
  name: string
  resourceName: string
}

export type ProviderData = {
  id: string
  name: string
  resourceName: string
  providerId: string
}

export type AppointmentTypeData = {
  id: string
  name: string
  duration: number
  enabled: boolean
  color: { bg: string; border: string }
}

export type AvailabilityData = {
  providerId: string
  operatoryId: string
}

export type AppointmentData = {
  id: string
  startTime: string
  endTime: string
  description: string
  operatoryId?: string
  providerResourceNames: string[]
  // Set when the practice cancelled or broke (no-show) the appointment — the
  // chair is free even though the PMS still lists the appointment.
  cancelled?: boolean
  broken?: boolean
}

// Column type for sorted display
type ColumnData = {
  id: string
  name: string
  resourceName: string
  type: `operatory` | `provider`
  providerId?: string
}

export type PMSSchedulesCalendarProps = {
  location: { name: string; id?: string }
  date: string
  schedules: {
    name: string
    blocks?: { startTime: string; endTime: string }[]
    notes?: { note: string; startTime: string; endTime: string; additionalData: { appointmentType: string } }[]
  }[]
  providers: ProviderData[]
  operatoriesPromise: Promise<OperatoryData[]> | null
  appointmentTypes: AppointmentTypeData[]
  availabilities: AvailabilityData[]
  appointmentsPromise: Promise<AppointmentData[]> | null
  showPageHeader?: boolean
}

function timeToMinutes(time: string): number {
  const [h, m] = time.split(`:`).map(Number)
  return h * 60 + m
}

function getSlotIndex(time: string): number {
  const mins = timeToMinutes(time)
  const startMins = 6 * 60 // 6:00
  return Math.floor((mins - startMins) / 30)
}

function getSlotSpan(startTime: string, endTime: string): number {
  const startMins = timeToMinutes(startTime)
  const endMins = timeToMinutes(endTime)
  return Math.max(1, Math.ceil((endMins - startMins) / 30))
}

type ScheduleColumnProps = {
  resourceName: string
  displayName: string
  resourceId: string
  type: `operatory` | `provider`
  isMuted: boolean
  isSelected: boolean
  onToggleSelect: () => void
  schedule?: {
    blocks?: { startTime: string; endTime: string }[]
    notes?: { note: string; startTime: string; endTime: string; additionalData: { appointmentType: string } }[]
  }
  appointments: AppointmentData[]
  appointmentTypeById: Map<string, AppointmentTypeData>
  providerByResourceName: Map<string, ProviderData>
}

function ScheduleColumn({
  resourceName,
  displayName,
  resourceId,
  type,
  isMuted,
  isSelected,
  onToggleSelect,
  schedule,
  appointments,
  appointmentTypeById,
  providerByResourceName,
}: ScheduleColumnProps): React.ReactElement {
  const slotHeight = 28

  // Parse comma-separated appointment type IDs (e.g., "appointmenttypes/31,appointmenttypes/32")
  const parseAppointmentTypeIds = (raw: string): string[] => {
    return raw.split(`,`).map((part) => part.trim().replace(/^appointmenttypes\//, ``))
  }

  const isSlotInSchedule = (slotIdx: number): boolean => {
    if (!schedule?.blocks?.length) return false
    const slotTime = TIME_SLOTS[slotIdx]
    const slotMins = timeToMinutes(slotTime)
    const slotEndMins = slotMins + 30
    return schedule.blocks.some((block) => {
      const blockStartMins = timeToMinutes(block.startTime)
      const blockEndMins = timeToMinutes(block.endTime)
      return slotMins < blockEndMins && slotEndMins > blockStartMins
    })
  }

  const blockoutSpans = useMemo(() => {
    if (!schedule?.notes) return []
    return schedule.notes
      .filter((note) => note.additionalData?.appointmentType)
      .map((note) => ({
        appointmentType: note.additionalData.appointmentType,
        startTime: note.startTime,
        endTime: note.endTime,
      }))
  }, [schedule])

  const getBlockoutsForSlot = (slotIdx: number): string[] => {
    if (!schedule?.notes) return []
    const slotTime = TIME_SLOTS[slotIdx]
    const slotMins = timeToMinutes(slotTime)
    const slotEndMins = slotMins + 30
    const types: string[] = []
    schedule.notes.forEach((note) => {
      if (!note.additionalData?.appointmentType) return
      const noteStartMins = timeToMinutes(note.startTime)
      const noteEndMins = timeToMinutes(note.endTime)
      if (slotMins < noteEndMins && slotEndMins > noteStartMins) {
        if (!types.includes(note.additionalData.appointmentType)) {
          types.push(note.additionalData.appointmentType)
        }
      }
    })
    return types
  }

  const positionedAppts = useMemo(() => {
    type ApptWithPosition = AppointmentData & { column: number; totalColumns: number }
    const positioned: ApptWithPosition[] = []

    const sorted = [...appointments].sort((a, b) => {
      const aStart = timeToMinutes(a.startTime)
      const bStart = timeToMinutes(b.startTime)
      if (aStart !== bStart) return aStart - bStart
      const aDur = timeToMinutes(a.endTime) - aStart
      const bDur = timeToMinutes(b.endTime) - bStart
      return bDur - aDur
    })

    sorted.forEach((appt) => {
      const apptStart = timeToMinutes(appt.startTime)
      const apptEnd = timeToMinutes(appt.endTime)
      const overlapping = positioned.filter((p) => {
        const pStart = timeToMinutes(p.startTime)
        const pEnd = timeToMinutes(p.endTime)
        return apptStart < pEnd && apptEnd > pStart
      })
      const usedCols = new Set(overlapping.map((p) => p.column))
      let col = 0
      while (usedCols.has(col)) col++
      positioned.push({ ...appt, column: col, totalColumns: 1 })
    })

    positioned.forEach((appt) => {
      const apptStart = timeToMinutes(appt.startTime)
      const apptEnd = timeToMinutes(appt.endTime)
      const overlapping = positioned.filter((p) => {
        const pStart = timeToMinutes(p.startTime)
        const pEnd = timeToMinutes(p.endTime)
        return apptStart < pEnd && apptEnd > pStart
      })
      const maxCol = Math.max(...overlapping.map((p) => p.column)) + 1
      overlapping.forEach((p) => {
        p.totalColumns = Math.max(p.totalColumns, maxCol)
      })
    })

    return positioned
  }, [appointments])

  const scheduledBg = isMuted ? `bg-surface-muted` : type === `operatory` ? `bg-blue-50` : `bg-green-50`
  const headerBg = isMuted ? `bg-surface-strong` : type === `operatory` ? `bg-blue-100` : `bg-green-100`

  return (
    <div className="flex min-w-[120px] flex-col border-r border-default last:border-r-0">
      <div className={`sticky top-0 z-10 border-b border-strong p-2 text-center text-xs font-medium ${headerBg}`}>
        <div className="flex items-center justify-between gap-1">
          <div className="min-w-0 flex-1 truncate text-left" title={`${resourceId} - ${displayName}`}>
            {resourceId} - {displayName}
          </div>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="h-3.5 w-3.5 flex-shrink-0 cursor-pointer rounded border-strong"
            title="Pin column to left"
          />
        </div>
      </div>
      <div className="relative" style={{ height: `${TIME_SLOTS.length * slotHeight}px` }}>
        {TIME_SLOTS.map((_, slotIdx) => {
          const inSchedule = isSlotInSchedule(slotIdx)
          const blockouts = type === `operatory` ? getBlockoutsForSlot(slotIdx) : []
          const blockoutNames = blockouts.flatMap((t) => {
            const vendorIds = parseAppointmentTypeIds(t)
            return vendorIds.map((id) => appointmentTypeById.get(id)?.name || id.replace(/_/g, ` `))
          })
          return (
            <div
              key={slotIdx}
              className={`absolute left-0 right-0 border-b border-default ${inSchedule ? scheduledBg : ``}`}
              style={{
                top: `${slotIdx * slotHeight}px`,
                height: `${slotHeight}px`,
                marginLeft: type === `operatory` ? `4px` : `0`,
              }}
              title={blockoutNames.length > 0 ? `Blockout: ${blockoutNames.join(`, `)}` : undefined}
            />
          )
        })}
        {type === `operatory` &&
          blockoutSpans.map((blockout, idx) => {
            const startIdx = getSlotIndex(blockout.startTime)
            const span = getSlotSpan(blockout.startTime, blockout.endTime)
            const top = startIdx * slotHeight
            const height = span * slotHeight
            const vendorIds = parseAppointmentTypeIds(blockout.appointmentType)
            const matchedTypes = vendorIds
              .map((id) => ({ id, type: appointmentTypeById.get(id) }))
              .filter((m) => m.type)
            // Show one bar per appointment type
            if (matchedTypes.length === 0) {
              return (
                <div
                  key={`blockout-${idx}`}
                  className="absolute left-0 z-10 w-1 bg-inverse/30"
                  style={{ top: `${top}px`, height: `${height}px` }}
                  title={blockout.appointmentType}
                />
              )
            }
            return matchedTypes.map((m, typeIdx) => (
              <div
                key={`blockout-${idx}-${m.id}`}
                className={`absolute z-10 w-1 ${m.type!.color.bg}`}
                style={{ top: `${top}px`, height: `${height}px`, left: `${typeIdx * 4}px` }}
                title={`${m.type!.name} (${m.id})`}
              />
            ))
          })}
        {positionedAppts.map((appt, idx) => {
          const startIdx = getSlotIndex(appt.startTime)
          const span = getSlotSpan(appt.startTime, appt.endTime)
          const top = startIdx * slotHeight
          const height = span * slotHeight
          const width = 100 / appt.totalColumns
          const left = appt.column * width
          const leftOffset = type === `operatory` ? 5 : 0
          const providerNames = appt.providerResourceNames
            .map((rn) => providerByResourceName.get(rn)?.name)
            .filter(Boolean)
            .join(`, `)
          const statusNote = appt.cancelled
            ? `Cancelled by practice — chair is free`
            : appt.broken
              ? `Broken (no-show) — chair is free`
              : null
          const tooltip = [
            appt.description,
            `${appt.startTime} - ${appt.endTime}`,
            providerNames ? `Provider: ${providerNames}` : null,
            statusNote,
          ]
            .filter(Boolean)
            .join(`\n`)
          const isFree = !!(appt.cancelled || appt.broken)
          return (
            <div
              key={appt.id || idx}
              className={`absolute overflow-hidden border-r border-(--surface) bg-surface-strong p-1 text-xs ${
                isMuted || isFree ? `opacity-50` : ``
              }`}
              style={{
                top: `${top}px`,
                height: `${height}px`,
                left: leftOffset > 0 ? `calc(${left}% + ${leftOffset}px)` : `${left}%`,
                width: leftOffset > 0 ? `calc(${width}% - ${leftOffset / appt.totalColumns}px)` : `${width}%`,
              }}
              title={tooltip}
            >
              <div className={`line-clamp-1 text-[10px] text-subtle ${isFree ? `line-through` : ``}`}>
                {appt.description}
                {isFree ? ` (${appt.cancelled ? `Cancelled` : `Broken`})` : ``}
              </div>
              <div className="text-[10px] text-subtle">
                {appt.startTime} - {appt.endTime}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const EMPTY_SCHEDULES: PMSSchedulesCalendarProps['schedules'] = []
const EMPTY_PROVIDERS: PMSSchedulesCalendarProps['providers'] = []
const EMPTY_APPOINTMENT_TYPES: PMSSchedulesCalendarProps['appointmentTypes'] = []

export default function PMSSchedulesCalendar({
  location,
  date,
  schedules = EMPTY_SCHEDULES,
  providers = EMPTY_PROVIDERS,
  operatoriesPromise,
  appointmentTypes = EMPTY_APPOINTMENT_TYPES,
  availabilities,
  appointmentsPromise,
  showPageHeader = false,
}: PMSSchedulesCalendarProps): React.ReactElement {
  // Use deferred data hooks for PMS API calls with slow/error tracking
  const {
    data: operatories,
    error: operatoriesError,
    isSlow: operatoriesSlow,
  } = useDeferredDataWithStatus<OperatoryData[]>(operatoriesPromise)
  const {
    data: appointments,
    error: appointmentsError,
    isSlow: appointmentsSlow,
  } = useDeferredDataWithStatus<AppointmentData[]>(appointmentsPromise)

  const pmsError = operatoriesError || appointmentsError
  const pmsSlow = (operatoriesSlow || appointmentsSlow) && !pmsError

  const fetcher = { state: `idle`, data: null, Form: `form` } as any
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)
  const [showOnlyEnabled, setShowOnlyEnabled] = useState(true)

  const isReloading = fetcher.state === `submitting` || fetcher.state === `loading`
  const isNavigating = false

  // Track selected columns (by resourceName)
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set())

  const toggleColumnSelection = (resourceName: string) => {
    setSelectedColumns((prev) => {
      const next = new Set(prev)
      if (next.has(resourceName)) {
        next.delete(resourceName)
      } else {
        next.add(resourceName)
      }
      return next
    })
  }

  // Consolidated lookups for schedules, appointment types, and providers
  const lookups = useMemo(() => {
    const scheduleByResource = new Map<string, (typeof schedules)[0]>()
    schedules.forEach((s) => scheduleByResource.set(s.name, s))

    const appointmentTypeById = new Map<string, AppointmentTypeData>()
    appointmentTypes.forEach((at) => appointmentTypeById.set(at.id, at))

    const providerByResourceName = new Map<string, ProviderData>()
    providers.forEach((p) => providerByResourceName.set(p.resourceName, p))

    return { scheduleByResource, appointmentTypeById, providerByResourceName }
  }, [schedules, appointmentTypes, providers])

  // Consolidated column selection and association logic
  const columnSelection = useMemo(() => {
    const selectedProvidersList = providers.filter((p) => selectedColumns.has(p.resourceName))
    const selectedOperatoriesList = (operatories || []).filter((o) => selectedColumns.has(o.resourceName))

    const singleSelectedProvider =
      selectedProvidersList.length === 1 && selectedOperatoriesList.length === 0 ? selectedProvidersList[0] : null
    const singleSelectedOperatory =
      selectedOperatoriesList.length === 1 && selectedProvidersList.length === 0 ? selectedOperatoriesList[0] : null

    // Get operatory IDs associated with the single selected provider
    const associatedOperatoryIds = new Set<string>()
    if (singleSelectedProvider) {
      availabilities
        .filter((a) => a.providerId === singleSelectedProvider.providerId)
        .forEach((a) => associatedOperatoryIds.add(a.operatoryId))
    }

    // Get provider IDs associated with the single selected operatory
    const associatedProviderIds = new Set<string>()
    if (singleSelectedOperatory) {
      availabilities
        .filter((a) => a.operatoryId === singleSelectedOperatory.id)
        .forEach((a) => associatedProviderIds.add(a.providerId))
    }

    // Build and sort all columns
    const allColumns: ColumnData[] = [
      ...(operatories || []).map((op) => ({ ...op, type: `operatory` as const, providerId: undefined })),
      ...providers.map((prov) => ({
        id: prov.id,
        name: prov.name,
        resourceName: prov.resourceName,
        type: `provider` as const,
        providerId: prov.providerId,
      })),
    ]

    const sortedColumns = allColumns.sort((a, b) => {
      const aSelected = selectedColumns.has(a.resourceName)
      const bSelected = selectedColumns.has(b.resourceName)
      if (aSelected && !bSelected) return -1
      if (!aSelected && bSelected) return 1

      // When a single provider is selected, sort associated operatories to the left
      if (singleSelectedProvider && a.type === `operatory` && b.type === `operatory`) {
        const aAssociated = associatedOperatoryIds.has(a.id)
        const bAssociated = associatedOperatoryIds.has(b.id)
        if (aAssociated && !bAssociated) return -1
        if (!aAssociated && bAssociated) return 1
      }

      // When a single operatory is selected, sort associated providers to the left
      if (singleSelectedOperatory && a.type === `provider` && b.type === `provider`) {
        const aAssociated = a.providerId ? associatedProviderIds.has(a.providerId) : false
        const bAssociated = b.providerId ? associatedProviderIds.has(b.providerId) : false
        if (aAssociated && !bAssociated) return -1
        if (!aAssociated && bAssociated) return 1
      }

      // Keep original order within selected and unselected groups
      // (operatories before providers within each group)
      if (a.type !== b.type) {
        return a.type === `operatory` ? -1 : 1
      }
      return 0
    })

    return {
      sortedColumns,
      singleSelectedProvider,
      singleSelectedOperatory,
      associatedOperatoryIds,
      associatedProviderIds,
    }
  }, [operatories, providers, selectedColumns, availabilities])

  const floatingData = useFloating({
    open: isDatePickerOpen,
    placement: `bottom-end`,
    strategy: `fixed`,
    elements: { reference: referenceElement, floating: popperElement },
    onOpenChange: setIsDatePickerOpen,
    middleware: [offset(5)],
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(floatingData.context),
    useDismiss(floatingData.context),
  ])

  function changeDay(direction: `prev` | `next`) {
    const params = new URLSearchParams(searchParams)
    const currentDate = params.get(`date`) || moment().format(`YYYY-MM-DD`)
    const newDate = moment(currentDate)
      .add(direction === `next` ? 1 : -1, `day`)
      .format(`YYYY-MM-DD`)
    params.set(`date`, newDate)
    navigate(`?${params.toString()}`)
  }

  function jumpToDate(newDate: Date) {
    const params = new URLSearchParams(searchParams)
    params.set(`date`, moment(newDate).format(`YYYY-MM-DD`))
    navigate(`?${params.toString()}`)
    setIsDatePickerOpen(false)
  }

  return (
    <div className="flex h-full flex-col">
      {showPageHeader && <PageHeader title={`PMS Schedules - ${location.name}`} />}
      <div className="flex min-h-0 flex-1 flex-col bg-surface p-6 shadow">
        {/* Day Navigation */}
        <div className="mb-4 flex flex-none items-center gap-4">
          <Button onClick={() => changeDay(`prev`)} mode="secondary">
            ← Prev
          </Button>
          <div className="flex-1 text-center text-lg font-medium">{moment(date).format(`dddd, MMMM D, YYYY`)}</div>
          <Button onClick={() => changeDay(`next`)} mode="secondary">
            Next →
          </Button>
          <Popover className="relative">
            <Popover.Button
              ref={setReferenceElement}
              className="flex items-center gap-2 rounded border border-strong bg-surface px-4 py-2 hover:bg-surface-subtle"
              {...getReferenceProps()}
              onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
            >
              <CalendarIcon className="h-4 w-4" />
              <span>{moment(date).format(`MMM D, YYYY`)}</span>
            </Popover.Button>
            {isDatePickerOpen && (
              <FloatingPortal>
                <Popover.Panel
                  ref={setPopperElement}
                  className="z-50 h-fit w-80 rounded-lg border border-default bg-surface-raised p-2 shadow-lg"
                  style={floatingData.floatingStyles}
                  {...getFloatingProps()}
                  static
                >
                  <CalendarPicker
                    date={date}
                    onDateChange={jumpToDate}
                    limitToMonth={false}
                    allowPastDates={true}
                    allowFutureDates={true}
                  />
                </Popover.Panel>
              </FloatingPortal>
            )}
          </Popover>
          <fetcher.Form method="post">
            <input type="hidden" name="date" value={date} />
            <Button type="submit" mode="primary" disabled={isReloading}>
              <RefreshDouble className="h-4 w-4" />
              {isReloading ? `Reloading...` : `Reload from PMS`}
            </Button>
          </fetcher.Form>
        </div>

        {fetcher.data && typeof fetcher.data === `object` && `message` in fetcher.data && fetcher.data.message ? (
          <div className="mb-4 flex-none rounded border border-green-300 bg-green-50 p-3 text-sm text-green-800">
            {String(fetcher.data.message)}
          </div>
        ) : null}
        {fetcher.data && typeof fetcher.data === `object` && `error` in fetcher.data && fetcher.data.error ? (
          <div className="mb-4 flex-none rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
            {String(fetcher.data.error)}
          </div>
        ) : null}

        {/* PMS loading/error banners */}
        {pmsError && (
          <div className="mb-4 flex flex-none items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <WarningTriangle className="h-5 w-5 flex-shrink-0 text-red-600" />
            <p className="text-sm text-red-800">
              Unable to load data from your Practice Management System. The PMS connection may be offline or
              experiencing issues. Schedule data and appointments may not be displayed.
            </p>
          </div>
        )}
        {pmsSlow && (
          <div className="mb-4 flex flex-none items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <RefreshDouble className="h-5 w-5 flex-shrink-0 animate-spin text-amber-600" />
            <p className="text-sm text-amber-800">
              Loading data from your Practice Management System is taking longer than expected. Please wait...
            </p>
          </div>
        )}

        {/* Appointment Type Legend */}
        {appointmentTypes.length > 0 && (
          <div className="mb-4 flex flex-none flex-wrap items-center gap-4 rounded border border-default bg-surface-subtle px-4 py-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-subtle">Appointment Types:</span>
              <button
                onClick={() => setShowOnlyEnabled(!showOnlyEnabled)}
                className={`rounded px-2 py-0.5 text-xs ${
                  showOnlyEnabled ? `bg-blue-100 text-blue-700` : `bg-surface-strong text-subtle`
                }`}
              >
                {showOnlyEnabled ? `Enabled` : `All`}
              </button>
            </div>
            {appointmentTypes
              .filter((at) => !showOnlyEnabled || at.enabled)
              .map((at) => (
                <Tooltip
                  key={at.id}
                  message={
                    <span className="rounded bg-inverse px-2 py-1 text-xs text-inverse">
                      {`ID: ${at.id} | ${at.duration ? `Duration: ${at.duration} min` : `No duration`}`}
                    </span>
                  }
                  placement="top"
                >
                  <div className="flex items-center gap-1.5">
                    <div className={`h-4 w-4 rounded border-l-4 ${at.color.bg} ${at.color.border}`} />
                    <span className="text-xs text-default">{at.name}</span>
                  </div>
                </Tooltip>
              ))}
          </div>
        )}

        {/* Calendar Grid */}
        <div className="relative min-h-0 flex-1 overflow-auto rounded border border-strong">
          {isNavigating && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-surface/70">
              <div className="flex items-center gap-2 rounded bg-surface px-4 py-2 shadow">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-strong border-t-blue-600" />
                <span className="text-sm text-subtle">Loading...</span>
              </div>
            </div>
          )}
          <div className="flex">
            {/* Time column */}
            <div className="sticky left-0 z-20 flex flex-col border-r border-strong bg-surface">
              <div className="sticky top-0 z-30 border-b border-strong bg-surface-muted p-2 text-center text-xs font-medium">
                Time
              </div>
              {TIME_SLOTS.map((time) => (
                <div
                  key={time}
                  className="flex h-7 items-center justify-center border-b border-default bg-surface-subtle px-2 text-xs text-subtle"
                >
                  {time}
                </div>
              ))}
            </div>

            {/* All columns */}
            {columnSelection.sortedColumns.map((col) => {
              const isMutedOp =
                columnSelection.singleSelectedProvider &&
                col.type === `operatory` &&
                !columnSelection.associatedOperatoryIds.has(col.id)
              const isMutedProv =
                columnSelection.singleSelectedOperatory &&
                col.type === `provider` &&
                col.providerId &&
                !columnSelection.associatedProviderIds.has(col.providerId)
              const colAppointments =
                col.type === `operatory`
                  ? (appointments || []).filter((a) => a.operatoryId === col.id)
                  : (appointments || []).filter((a) => a.providerResourceNames.includes(col.resourceName))

              return (
                <ScheduleColumn
                  key={col.resourceName}
                  resourceName={col.resourceName}
                  displayName={col.name}
                  resourceId={col.id}
                  type={col.type}
                  isMuted={!!(isMutedOp || isMutedProv)}
                  isSelected={selectedColumns.has(col.resourceName)}
                  onToggleSelect={() => toggleColumnSelection(col.resourceName)}
                  schedule={lookups.scheduleByResource.get(col.resourceName)}
                  appointments={colAppointments}
                  appointmentTypeById={lookups.appointmentTypeById}
                  providerByResourceName={lookups.providerByResourceName}
                />
              )
            })}

            {/* Empty state */}
            {(operatories?.length || 0) === 0 && providers.length === 0 && !pmsSlow && !pmsError && (
              <div className="flex flex-1 items-center justify-center p-8 text-subtle">
                No operatories or providers configured for this location
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
